export function numericCell(value) {
  if (typeof value === "boolean" || value == null) return null
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  const text = String(value).replace(/[\s,]/g, "")
  if (!text) return null
  const number = Number(text)
  return Number.isFinite(number) ? number : null
}

// Explicit column format. `locale` is optional and falls back to en-US, as before.
export function formattedCell(value, config, locale) {
  if (!config || config.format === "auto" || config.format === "text") return String(value ?? "")
  const number = numericCell(value)
  if (number == null) return String(value ?? "")
  const decimals = config.format === "integer" ? 0 : Math.max(0, Math.min(6, Number(config.decimals ?? 2) || 0))
  const result = formatDecimal(number, decimals, locale)
  return config.format === "percent" ? `${result}%` : result
}

// ---------------------------------------------------------------------------
// Table defaults: human headers and inferred number formats.
//
// Ported from the summer compliance-ui DynamicCustom table (DynamicReportTable
// `formatCellValue` / `numericColumnKeys`, utils/formatters.js, the KPI percent rule)
// and extended so a table reads well when the agent sends no columnLabels or
// columnFormats. Explicit columnLabels and columnFormats (other than "auto") always win.
// ---------------------------------------------------------------------------

const ABBREVIATIONS = {
  nav: "NAV",
  pe: "P/E",
  pb: "P/B",
  mv: "MV",
  qar: "QAR",
  usd: "USD",
  eur: "EUR",
  gbp: "GBP",
  isin: "ISIN",
  cusip: "CUSIP",
  sedol: "SEDOL",
  ytd: "YTD",
  mtd: "MTD",
  qtd: "QTD",
  ttm: "TTM",
  pnl: "P&L",
  aum: "AUM",
  eps: "EPS",
  roe: "ROE",
  roa: "ROA",
  ebitda: "EBITDA",
  esg: "ESG",
  fx: "FX",
  gics: "GICS",
  etf: "ETF",
  id: "ID",
  kpi: "KPI",
}
// A trailing unit token becomes ", <unit>": `weight_pct` → "Weight, %".
const UNIT_SUFFIXES = { pct: "%", percent: "%", percentage: "%", qar: "QAR", usd: "USD", eur: "EUR", gbp: "GBP", bps: "bps" }
const NUMBER_WORDS = new Set(["one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"])
const PERIOD_WORDS = new Set(["day", "week", "month", "quarter", "year"])

export function columnNameTokens(column) {
  return String(column ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/[_\s\-.]+/)
    .map((token) => token.toLowerCase())
    .filter(Boolean)
}

// `benchmark_one_month_price_return_pct` → "Benchmark one-month price return, %".
// Sentence case; known abbreviations upper-cased (pnl → P&L, pe / p_e → P/E); a trailing
// pct / percent → ", %" and a trailing currency → ", QAR". A key that already reads as
// text (has a space and no underscore) is returned unchanged.
export function humanizeColumnName(column) {
  const raw = String(column ?? "").trim()
  if (!raw) return ""
  if (/\s/.test(raw) && !raw.includes("_")) return raw
  const tokens = columnNameTokens(raw).reduce((merged, token) => {
    // `p_e` / `p_b` are one ratio token.
    if ((token === "e" || token === "b") && merged[merged.length - 1] === "p") return [...merged.slice(0, -1), `p${token}`]
    return [...merged, token]
  }, [])
  const unit = tokens.length > 1 ? UNIT_SUFFIXES[tokens[tokens.length - 1]] : undefined
  const body = unit ? tokens.slice(0, -1) : tokens
  const words = body.flatMap((token, index) => {
    const previous = body[index - 1]
    const next = body[index + 1]
    const isCount = (word) => NUMBER_WORDS.has(word) || /^\d+$/.test(word)
    const isPeriod = (word) => Boolean(word) && PERIOD_WORDS.has(word.replace(/s$/, ""))
    // "one month" → "one-month"; the period word is consumed by the count before it.
    if (isCount(token) && isPeriod(next)) return [`${token}-${next}`]
    if (isPeriod(token) && previous && isCount(previous)) return []
    if (/^\d+[dwmqy]$/.test(token)) return [token.toUpperCase()]
    if (UNIT_SUFFIXES[token] === "%") return ["%"]
    return [ABBREVIATIONS[token] || token]
  })
  const text = words.join(" ")
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}${unit ? `, ${unit}` : ""}`
}

export function isBlankCell(value) {
  return value == null || (typeof value === "string" && value.trim() === "")
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)?)?$/
const PERCENT_WORDS = new Set(["weight", "weights", "return", "returns", "yield", "yields", "ytm"])
const MONEY_WORDS = new Set(["value", "contribution", "pnl", "amount", "mv", "aum", "cost"])
const CURRENCY_TOKENS = new Set(["qar", "usd", "eur", "gbp"])
const RATIO_WORDS = new Set(["ratio", "pe", "pb", "beta", "sharpe", "multiple"])
const PLAIN_INTEGER_WORDS = new Set(["year", "id", "code"])
const LONG_TEXT_WORDS = new Set([
  "methodology",
  "reason",
  "reasons",
  "description",
  "comment",
  "comments",
  "commentary",
  "note",
  "notes",
  "explanation",
  "rationale",
  "details",
  "text",
])

// Infers a display format for one column from its name and values.
// Returns { kind, decimals?, scale?, grouping?, long? }, kind being one of
// "percent" | "money" | "ratio" | "integer" | "number" | "date" | "text".
//
// Heuristic, in order:
// 1. No non-blank values → text.
// 2. Every value is an ISO date (YYYY-MM-DD, optionally a midnight time) → date.
// 3. Any value is not numeric, or a numeric string has a leading zero ("0012" is a code) → text.
//    It is "long" text when the name has a prose word (methodology, reason, notes…), or the
//    longest value is over longText.maxChars, or the average is over longText.averageChars.
// 4. Numbers, by name. An explicit unit token decides first: pct/percent → percent;
//    qar/usd/eur/gbp → money. Otherwise the LAST keyword in the name decides (the English head
//    noun), so `return_contribution` is money and `contribution_to_return` is a percent:
//      weight / return / yield → percent, 2 decimals;
//      value / contribution / pnl / amount / mv / aum / cost → money, grouped, 0 decimals when
//        every value is whole, otherwise 2;
//      ratio / pe / p_e / pb / beta / sharpe / multiple → ratio, 2 decimals.
//    Percent units. A pct/percent column is already in percent units (25 → "25.00%"), the same
//    rule as the explicit "percent" format. A column that is a percent only by its word (weight,
//    return, yield) uses the summer KPI rule, applied to the whole column so it stays consistent:
//    when every value is within [-1, 1] the column holds fractions and is shown ×100
//    (0.0738 → "7.38%"); otherwise the values are shown as they are (18.28 → "18.28%").
//    Known ambiguity: a word-named percent column whose values are all tiny real percents
//    (every |v| ≤ 1) is read as fractions; the agent should name it `_pct` or send columnFormats.
// 5. Other numbers: all whole → integer (grouped, except year / id / code); otherwise 2 decimals
//    (the summer default).
export function inferColumnFormat(column, values, longText = { maxChars: 60, averageChars: 40 }) {
  const present = (values || []).filter((value) => !isBlankCell(value))
  if (!present.length) return { kind: "text" }
  const tokens = columnNameTokens(column)
  if (present.every((value) => typeof value === "string" && ISO_DATE.test(value.trim()))) return { kind: "date" }
  const numbers = present.map((value) =>
    typeof value === "string" && /^[+-]?0\d/.test(value.trim()) ? null : numericCell(value),
  )
  if (numbers.some((number) => number == null)) {
    const lengths = present.map((value) => String(value).length)
    const average = lengths.reduce((sum, length) => sum + length, 0) / lengths.length
    const longest = lengths.reduce((max, length) => Math.max(max, length), 0)
    const named = tokens.some((token) => LONG_TEXT_WORDS.has(token))
    return { kind: "text", long: named || longest > longText.maxChars || average > longText.averageChars }
  }
  const whole = numbers.every((number) => Number.isInteger(number))
  const namedPercent = tokens.some((token) => UNIT_SUFFIXES[token] === "%")
  const percent = () => {
    const fraction =
      !namedPercent && numbers.every((number) => Math.abs(number) <= 1) && numbers.some((number) => number !== 0)
    return { kind: "percent", decimals: 2, scale: fraction ? 100 : 1 }
  }
  const money = () => ({ kind: "money", decimals: whole ? 0 : 2 })
  if (namedPercent) return percent()
  if (tokens.some((token) => CURRENCY_TOKENS.has(token))) return money()
  const keyword = [...tokens.join(" ").replace(/\bp (e|b)\b/g, "p$1").split(" ")]
    .reverse()
    .find((token) => PERCENT_WORDS.has(token) || MONEY_WORDS.has(token) || RATIO_WORDS.has(token))
  if (keyword && PERCENT_WORDS.has(keyword)) return percent()
  if (keyword && MONEY_WORDS.has(keyword)) return money()
  if (keyword) return { kind: "ratio", decimals: 2 }
  if (whole) return { kind: "integer", decimals: 0, grouping: !tokens.some((token) => PLAIN_INTEGER_WORDS.has(token)) }
  return { kind: "number", decimals: 2 }
}

export function isNumericFormat(format) {
  return Boolean(format && ["percent", "money", "ratio", "integer", "number"].includes(format.kind))
}

const SIGNED_PERCENT_FORMATS = new Set(["percent_change", "return"])

// An explicit columnFormats entry in the shape inferColumnFormat returns.
// A missing or "auto" entry returns null so the inferred default applies.
export function explicitColumnFormat(config) {
  if (!config || !config.format || config.format === "auto") return null
  if (config.format === "text") return { kind: "text", explicit: true }
  if (config.format === "integer") return { kind: "integer", decimals: 0, explicit: true }
  const decimals = Math.max(0, Math.min(6, Number(config.decimals ?? 2) || 0))
  // "percent_change" / "return": a percent that always shows ▲/▼ (backlog #7).
  if (SIGNED_PERCENT_FORMATS.has(config.format)) return { kind: "percent", decimals, explicit: true, signed: true }
  return { kind: config.format === "percent" ? "percent" : "number", decimals, explicit: true }
}

// Formats one cell with a format from inferColumnFormat / explicitColumnFormat.
// Returns null for a blank cell (null, undefined, "") so the caller can draw the muted
// empty mark; a real 0 is formatted like any other number.
export function formatTableCell(value, format, locale) {
  if (isBlankCell(value)) return null
  if (!format || format.kind === "text") return String(value)
  if (format.kind === "date") return typeof value === "string" ? formatIsoDate(value, locale) : String(value)
  const number = numericCell(value)
  if (number == null) return String(value)
  const text = formatDecimal(number * (format.scale || 1), format.decimals ?? 2, locale, format.grouping !== false)
  return format.kind === "percent" ? `${text}%` : text
}

// Label, format and alignment for every column of a dataset-backed table. Inference reads
// at most `sampleRows` rows so large tables stay cheap.
export function tableColumnModels(headers, rows, data = {}, options = {}) {
  const sampled = rows.slice(0, Math.max(1, options.sampleRows || 1000))
  return headers.map((header) => {
    const inferred = inferColumnFormat(
      header,
      sampled.map((row) => row[header]),
      options.longText,
    )
    const format = explicitColumnFormat(data.columnFormats?.[header]) || inferred
    const numeric = isNumericFormat(format)
    const label = String(data.columnLabels?.[header] ?? "").trim() || humanizeColumnName(header) || header
    return {
      key: header,
      label,
      format,
      // Returns, changes and deltas in percent show ▲/▼ in green / red (weights never do).
      signed: isSignedPercentColumn(header, format, label, data.columnFormats?.[header]),
      inferred,
      numeric,
      long: format.kind === "text" && Boolean(inferred.long),
      nowrap: numeric || format.kind === "date",
    }
  })
}

const numberFormatters = new Map()
function formatDecimal(number, decimals, locale, grouping = true) {
  const key = `${locale || ""}|${decimals}|${grouping}`
  const formatter =
    numberFormatters.get(key) ||
    numberFormatters
      .set(
        key,
        safeIntl(locale, (tag) =>
          new Intl.NumberFormat(tag, { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: grouping }),
        ),
      )
      .get(key)
  // Values that round to zero print as "0", never "-0" or "-0.00".
  return formatter.format(Math.abs(number) < 0.5 * 10 ** -decimals ? 0 : number)
}

const dateFormatters = new Map()
function formatIsoDate(text, locale) {
  const match = ISO_DATE.exec(text.trim())
  if (!match) return text
  const key = locale || ""
  const formatter =
    dateFormatters.get(key) ||
    dateFormatters
      .set(
        key,
        safeIntl(locale, (tag) => new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })),
      )
      .get(key)
  return formatter.format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))))
}

// The dashboard's locale comes from the host (language.intl()); an unknown tag falls back to en-US.
function safeIntl(locale, create) {
  try {
    return create(locale || "en-US")
  } catch {
    return create("en-US")
  }
}

export function tableSummary(rows, headers, operations = {}) {
  return Object.fromEntries(
    headers.map((header) => {
      const operation = operations[header] || "none"
      if (operation === "none") return [header, ""]
      if (operation === "count") return [header, rows.length]
      const numbers = rows.map((row) => numericCell(row[header])).filter((number) => number !== null)
      if (!numbers.length) return [header, ""]
      if (operation === "sum") return [header, numbers.reduce((sum, number) => sum + number, 0)]
      if (operation === "avg") return [header, numbers.reduce((sum, number) => sum + number, 0) / numbers.length]
      if (operation === "min") return [header, numbers.reduce((min, number) => Math.min(min, number), Infinity)]
      if (operation === "max") return [header, numbers.reduce((max, number) => Math.max(max, number), -Infinity)]
      return [header, ""]
    }),
  )
}

export function chartItems(rows, data, type, labels) {
  const measures =
    data?.measures?.length && ["bar", "line", "multiSeries"].includes(type)
      ? data.measures
      : [data?.metric || (data?.aggregation === "count" ? "__count__" : "")].filter(Boolean)
  if (type === "scatter") {
    const x = data?.xColumn
    const y = data?.yColumn
    if (!x || !y || x === y) return []
    return rows.flatMap((row) => {
      const xValue = numericCell(row[x])
      const yValue = numericCell(row[y])
      const size = data?.sizeColumn ? numericCell(row[data.sizeColumn]) : null
      if (xValue == null || yValue == null || (data?.sizeColumn && size == null)) return []
      return [{ name: String(row[data?.label] ?? ""), value: yValue, x: xValue, size }]
    })
  }
  if (!data?.label || !rows.length || !measures.length) return []
  const rankedMeasures = data?.topBy && !measures.includes(data.topBy) ? [...measures, data.topBy] : measures
  // A category's rank is determined by its aggregate, never by the first row encountered.
  const groups = new Map()
  rows.forEach((row) => {
    const name = String(row[data.label] ?? labels.itemLabel.replace("{count}", String(groups.size + 1)))
    if (!groups.has(name)) groups.set(name, Object.fromEntries(rankedMeasures.map((measure) => [measure, []])))
    rankedMeasures.forEach((measure) => {
      const number = numericCell(row[measure])
      if (data?.aggregation === "count") groups.get(name)[measure].push(1)
      else if (number != null) groups.get(name)[measure].push(number)
    })
  })
  const aggregate = (values) => {
    if (data?.aggregation === "count") return values.length
    // No number for this category (a weekend in a daily series): a gap, not a fake 0.
    if (!values.length) return null
    if (data?.aggregation === "avg") return values.reduce((sum, value) => sum + value, 0) / values.length
    if (data?.aggregation === "min") return values.reduce((min, value) => Math.min(min, value), Infinity)
    if (data?.aggregation === "max") return values.reduce((max, value) => Math.max(max, value), -Infinity)
    return values.reduce((sum, value) => sum + value, 0)
  }
  const items = [...groups].map(([name, values]) => ({
    name,
    values: Object.fromEntries(rankedMeasures.map((measure) => [measure, aggregate(values[measure])])),
    value: aggregate(values[measures[0]] || []) ?? 0,
  }))
  if (data?.topBy)
    items.sort((first, second) => {
      const value = (first.values[data.topBy] ?? first.value) - (second.values[data.topBy] ?? second.value)
      return data.topOrder === "asc" ? value : -value
    })
  return capChartItems(items, data, type, labels)
}

// Default category cap of a chart (main's 12). An explicit `showTop` wins; Infinity shows all;
// `limitEnabled: false` turns the cap off.
export const CHART_DEFAULT_TOP = 12
// Part-of-whole charts: the categories past the cap become one "Other" slice, so the slices
// still add up to the whole (a 15-sector allocation pie stays 100%).
const WHOLE_CHART_TYPES = ["pie", "treemap"]

// Applies the cap. The result carries `total` (categories before the cap) and `capped`, so the
// widget can say "Top 12 of 15" for ranked charts.
export function capChartItems(items, data, type, labels) {
  const explicit = Number(data?.showTop)
  // `limitEnabled: false` (summer `limit_enabled`) switches the cap off: every category shows.
  const cap = data?.limitEnabled === false ? Infinity : explicit > 0 ? explicit : CHART_DEFAULT_TOP
  const total = items.length
  if (total <= cap) return Object.assign(items, { total, capped: false })
  if (WHOLE_CHART_TYPES.includes(type)) {
    // Largest first (unless the author ranked them), so the tail that is grouped is the small slices.
    const ranked = data?.topBy ? items : [...items].sort((first, second) => (second.value ?? 0) - (first.value ?? 0))
    const head = ranked.slice(0, cap)
    const tail = ranked.slice(cap)
    const sum = (values) => values.reduce((value, next) => value + (Number.isFinite(next) ? next : 0), 0)
    const other = {
      name: labels?.chartOther || "Other",
      other: true,
      count: tail.length,
      value: sum(tail.map((item) => item.value)),
      values: Object.fromEntries(
        Object.keys(tail[0]?.values || {}).map((measure) => [measure, sum(tail.map((item) => item.values[measure]))]),
      ),
    }
    return Object.assign([...head, other], { total, capped: false, grouped: tail.length })
  }
  return Object.assign(items.slice(0, cap), { total, capped: true })
}

// "Top 12 of 15" for a ranked chart that does not show every category.
export function chartCapCaption(items, labels) {
  if (!items?.capped) return ""
  return (labels?.chartTopOf || "Top {shown} of {total}")
    .replace("{shown}", String(items.length))
    .replace("{total}", String(items.total))
}

export function removeFreeStyleAxis(value, axis, removed) {
  const cellAlign = Object.fromEntries(
    Object.entries(value.cellAlign || {}).flatMap(([key, align]) => {
      const [row, col] = key.split(":").map(Number)
      if (!Number.isInteger(row) || !Number.isInteger(col)) return []
      if ((axis === "row" && row === removed) || (axis === "column" && col === removed)) return []
      return [
        [
          `${row - (axis === "row" && row > removed ? 1 : 0)}:${col - (axis === "column" && col > removed ? 1 : 0)}`,
          align,
        ],
      ]
    }),
  )
  return axis === "row"
    ? {
        ...value,
        rows: value.rows.filter((_, index) => index !== removed),
        rowTypes: value.rowTypes.filter((_, index) => index !== removed),
        cellAlign,
      }
    : {
        ...value,
        headers: value.headers.filter((_, index) => index !== removed),
        columnWidths: value.columnWidths.filter((_, index) => index !== removed),
        columnTypes: value.columnTypes.filter((_, index) => index !== removed),
        rows: value.rows.map((row) => row.filter((_, index) => index !== removed)),
        cellAlign,
      }
}

// Smallest width (px) at which `text` wraps into at most `lines` lines, breaking at spaces and
// after hyphens like the browser does.
function wrapWidth(text, lines, measure) {
  const parts = String(text).split(/(?<=-)|\s+/).filter(Boolean)
  if (!parts.length) return 0
  const space = measure(" ", "body")
  const widths = parts.map((part) => measure(part, "body"))
  const joins = String(text)
    .split(/\s+/)
    .flatMap((word) => word.split(/(?<=-)/).map((piece, index) => (index ? 0 : space)))
  const count = (limit) => {
    let used = 0
    let total = 1
    widths.forEach((width, index) => {
      const next = used ? used + joins[index] + width : width
      if (used && next > limit) {
        total += 1
        used = width
      } else used = next
    })
    return total
  }
  let low = Math.max(...widths)
  let high = widths.reduce((sum, width, index) => sum + width + (index ? joins[index] : 0), 0)
  if (count(low) <= lines) return low
  for (let step = 0; step < 24 && high - low > 0.5; step += 1) {
    const middle = (low + high) / 2
    if (count(middle) <= lines) high = middle
    else low = middle
  }
  return high
}

// Column widths for a table that must fit `available` px (display only, table-layout: fixed).
// `columns` are tableColumnModels; `texts[i]` the formatted cells of column i; `measure(text, "head" | "body")`
// the rendered text width in px; `padding` the horizontal cell padding (both sides).
// Each column gets three widths:
// - floor: what it cannot go below (nowrap content, the longest word, the header's longest word);
// - min: short headers on one line where that costs little, text in at most 3 lines;
// - ideal: header on one line, text in at most 2 lines, long text 160px.
// The space is shared floor → min → ideal → long text up to its max, then evenly.
export function tableColumnWidths(columns, texts, available, measure, options = {}) {
  const padding = options.padding ?? 20
  // A table too narrow to give every column its min first tries a tighter cell padding (summer compact 6px a side);
  // the result carries the padding it was computed with.
  const tight = options.tightPadding
  if (tight !== undefined && tight < padding) {
    const loose = tableColumnWidths(columns, texts, available, measure, { ...options, tightPadding: undefined })
    if (!loose.short) return loose
    // §75: a style that keeps its card inset on the outer cells of a tight table (Cards) reserves it here, and the
    // first and last columns carry it, so the table still spans `available` and no column loses a pixel to it.
    const inset = Math.max(0, options.tightInset || 0)
    const fitted = tableColumnWidths(columns, texts, available - 2 * inset, measure, { ...options, padding: tight, tightPadding: undefined })
    if (inset && fitted.length) {
      fitted[0] += inset
      fitted[fitted.length - 1] += inset
    }
    return fitted
  }
  const longMin = options.longMinPx ?? 88
  const longMax = options.longMaxPx ?? 320
  const word = (text, kind) =>
    String(text ?? "")
      // Browsers also break after a hyphen ("month-to-date"), so those parts count as words.
      .split(/\s+|(?<=-)/)
      .reduce((value, part) => Math.max(value, measure(part, kind)), 0)
  const specs = columns.map((column, index) => {
    const label = String(column.label ?? "")
    const headOne = Math.ceil(measure(label, "head")) + padding
    const headWord = Math.ceil(word(label, "head")) + padding
    const headTwo = Math.max(headWord, Math.ceil(measure(label, "head") / 2) + padding + 6)
    const values = (texts[index] || []).filter((text) => text != null && text !== "").map(String)
    const content = Math.ceil(values.reduce((value, text) => Math.max(value, measure(text, "body")), 0)) + padding
    const contentWord = Math.ceil(values.reduce((value, text) => Math.max(value, word(text, "body")), 0)) + padding
    if (column.nowrap || column.numeric) {
      const floor = Math.max(content, headWord)
      return { floor, min: Math.max(floor, Math.min(headOne, floor + 24)), ideal: Math.max(content, headOne), max: Math.max(content, headOne) }
    }
    if (column.long) {
      const floor = Math.max(60, headWord)
      const min = Math.max(longMin, headWord)
      const ideal = Math.max(min, headOne, Math.min(160, content))
      return { floor, min, ideal, max: Math.max(ideal, Math.min(longMax, content)) }
    }
    // Text: the narrowest width that wraps every value into at most 3 lines (min) or 2 (ideal).
    const linesWidth = (lines) =>
      Math.ceil(values.reduce((value, text) => Math.max(value, wrapWidth(text, lines, measure)), 0)) + padding
    const floor = Math.max(linesWidth(3), headWord)
    const min = Math.max(floor, headTwo)
    const ideal = Math.max(min, headOne, linesWidth(2))
    return { floor, min, ideal, max: Math.max(ideal, content) }
  })
  const widths = Object.assign(specs.map((spec) => spec.floor), { padding })
  const total = () => widths.reduce((sum, width) => sum + width, 0)
  if (!(available > 0) || total() >= available) return Object.assign(widths, { short: true })
  const raise = (key, only = () => true) => {
    const gaps = specs.map((spec, index) => (only(columns[index]) ? Math.max(0, spec[key] - widths[index]) : 0))
    const need = gaps.reduce((sum, gap) => sum + gap, 0)
    if (!need) return
    const share = Math.min(1, (available - total()) / need)
    gaps.forEach((gap, index) => (widths[index] += Math.floor(gap * share)))
  }
  // Text and numbers reach their min before long text (which is clamped with a tooltip anyway).
  raise("min", (column) => !column.long)
  raise("min")
  raise("ideal")
  // Short columns (codes, units, numbers, short text) stop at their content width.
  raise("max", (column) => !column.long)
  widths.short = widths.some((width, index) => width < specs[index].min)
  // Leftover space: a long-text column takes all of it (it is the one that wraps, so every
  // extra pixel saves lines). Without one, every column grows in proportion to its width (summer
  // `width: 100%` auto layout), so no single short column balloons. The last target takes the
  // rounding pixels, so the columns fill the table.
  const longColumns = columns.map((column, index) => (column.long ? index : -1)).filter((index) => index >= 0)
  const targets = longColumns.length ? longColumns : columns.map((_, index) => index)
  const rest = available - total()
  if (rest > 0 && targets.length) {
    const weight = targets.reduce((sum, index) => sum + widths[index], 0) || 1
    targets.forEach((index) => (widths[index] += Math.floor((rest * widths[index]) / weight)))
    widths[targets[targets.length - 1]] += available - total()
  }
  return widths
}

// A value axis with 3–5 round ticks that covers `values` (nulls ignored), for line charts:
// { min, max, interval }, or undefined when there is nothing to scale.
export function niceValueAxis(values, options = {}) {
  const minTicks = options.minTicks ?? 3
  const maxTicks = options.maxTicks ?? 5
  const numbers = values.filter((value) => typeof value === "number" && Number.isFinite(value))
  if (!numbers.length) return undefined
  let low = Math.min(...numbers)
  let high = Math.max(...numbers)
  if (low === high) {
    const pad = Math.abs(low) * 0.05 || 1
    low -= pad
    high += pad
  }
  const steps = []
  const magnitude = 10 ** Math.floor(Math.log10((high - low) / (maxTicks - 1)))
  for (const scale of [magnitude / 10, magnitude, magnitude * 10]) for (const base of [1, 2, 2.5, 5]) steps.push(base * scale)
  steps.sort((first, second) => first - second)
  for (const step of steps) {
    const min = Math.floor(low / step + 1e-9) * step
    const max = Math.ceil(high / step - 1e-9) * step
    const ticks = Math.round((max - min) / step) + 1
    if (ticks >= minTicks && ticks <= maxTicks) return { min: round(min), max: round(max), interval: round(step) }
  }
  return undefined
}

function round(value) {
  return Number(value.toPrecision(12))
}

// A category label shortened to `maxChars` without cutting mid-word where possible: the longest
// words lose their tail first ("Consumer Non-Cyclicals" → "Consumer Non-Cycl."), then the label is
// cut with an ellipsis. Short labels come back unchanged.
export function abbreviateLabel(label, maxChars) {
  const text = String(label ?? "")
  if (text.length <= maxChars || maxChars < 4) return text
  const words = text.split(/(\s+|-)/)
  for (let guard = 0; words.join("").length > maxChars && guard < 40; guard += 1) {
    let longest = -1
    words.forEach((word, index) => {
      if (/^(\s+|-)$/.test(word) || word.endsWith(".") && word.length <= 5) return
      if (longest < 0 || word.length > words[longest].length) longest = index
    })
    if (longest < 0 || words[longest].length <= 5) break
    const word = words[longest].replace(/\.$/, "")
    words[longest] = `${word.slice(0, Math.max(4, word.length - Math.max(2, words.join("").length - maxChars)) - 1)}.`
  }
  const short = words.join("")
  return short.length <= maxChars ? short : `${short.slice(0, maxChars - 1).trimEnd()}…`
}

// ── Table filters and the filter widget ─────────────────────────────────────────────────────

// A filter row the author has not finished (no column, or no value for an operator that needs
// one) is not applied, as in summer ("Not applied — fill in a value"), instead of matching only
// empty cells and blanking the table.
export function filterIsApplied(filter) {
  if (!filter?.column) return false
  if (filter.operator === "notEmpty") return true
  return String(filter.value ?? "").trim().length > 0
}

// A comparable form of a cell or a typed value: a number, an ISO date (its day, in ms), else null.
function filterComparable(value) {
  if (typeof value === "string" && ISO_DATE_PREFIX.test(value.trim())) {
    const time = Date.parse(value.trim().slice(0, 10))
    return Number.isFinite(time) ? time : null
  }
  return numericCell(value)
}
const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}(?:$|[T ])/
const ORDER_OPERATORS = new Set(["gt", "gte", "lt", "lte"])

// Review 2 §6: the scale a column is shown in. A column of fractions (0.052) reads "5.20%" (inferColumnFormat,
// scale 100), so the value the author types is in that scale: "Weight > 5" means 5%, not 500%.
function filterScale(format) {
  return format?.kind === "percent" && Number(format.scale) > 0 ? Number(format.scale) : 1
}

// `format` (optional): the column's shown format, from inferColumnFormat over the table (filterTableRows passes it).
// Without it, cells compare as stored, as before.
export function rowMatchesFilter(row, filter, format) {
  if (!filterIsApplied(filter)) return true
  const cell = row[filter.column]
  const value = String(cell ?? "")
  const operator = filter.operator || "contains"
  if (operator === "notEmpty") return value.trim().length > 0
  const expected = String(filter.value ?? "").trim().toLowerCase()
  // Kind-aware comparisons (summer, backlog #12): numbers by value ("2" = "2.00"), dates by day; text as text.
  const scale = filterScale(format)
  const stored = filterComparable(cell)
  const left = stored != null && scale !== 1 ? stored * scale : stored
  const right = filterComparable(String(filter.value ?? "").trim())
  const both = left != null && right != null
  // 0.052 × 100 is 5.2000000000000002: a scaled cell equals the typed value to 1e-9.
  const equal = both ? (scale === 1 ? left === right : Math.abs(left - right) <= 1e-9 * Math.max(1, Math.abs(right))) : value.trim().toLowerCase() === expected
  if (operator === "equals") return equal
  if (operator === "not_equals") return !equal
  if (ORDER_OPERATORS.has(operator)) {
    if (!both) return false
    if (operator === "gt") return left > right
    if (operator === "gte") return left >= right
    if (operator === "lt") return left < right
    return left <= right
  }
  return value.toLowerCase().includes(expected)
}

// A table's rows through its filter rows, each column read in the scale it is shown in (Review 2 §6). The format is
// the table's own (tableColumnModels): the author's column format, else inferred once from the first 1,000 rows.
export function filterTableRows(rows, filters, columnFormats) {
  const list = rows || []
  const formats = new Map()
  const formatOf = (column) => {
    if (!formats.has(column)) formats.set(column, explicitColumnFormat(columnFormats?.[column]) || inferColumnFormat(column, list.slice(0, 1000).map((row) => row?.[column])))
    return formats.get(column)
  }
  return (filters || []).reduce((current, filter) => {
    if (!filterIsApplied(filter)) return current
    const format = formatOf(filter.column)
    return current.filter((row) => rowMatchesFilter(row, filter, format))
  }, list)
}

// The kind of a table column for its filter row: "date" (ISO days), "number", or "text" (inferColumnFormat).
export function filterColumnKind(rows, column) {
  if (!column) return "text"
  const values = (rows || []).slice(0, 200).map((row) => row?.[column])
  const format = inferColumnFormat(column, values)
  if (format.kind === "date") return "date"
  return isNumericFormat(format) ? "number" : "text"
}

// The operators a filter row offers for a column kind (summer AddDashboardWidgetModal): text = / ≠ / contains;
// number and date = ≠ > ≥ < ≤, a date in words ("Before", "On or after"); every kind keeps "Not empty". `label` is
// the labels key, `text` its English fallback.
const FILTER_OPERATORS = {
  text: [
    { value: "contains", label: "filterContains", text: "Contains" },
    { value: "equals", label: "filterOpEq", text: "=" },
    { value: "not_equals", label: "filterOpNe", text: "≠" },
  ],
  number: [
    { value: "equals", label: "filterOpEq", text: "=" },
    { value: "not_equals", label: "filterOpNe", text: "≠" },
    { value: "gt", label: "filterOpGt", text: ">" },
    { value: "gte", label: "filterOpGte", text: "≥" },
    { value: "lt", label: "filterOpLt", text: "<" },
    { value: "lte", label: "filterOpLte", text: "≤" },
  ],
  date: [
    { value: "equals", label: "filterDateOn", text: "On" },
    { value: "not_equals", label: "filterDateNotOn", text: "Not on" },
    { value: "lt", label: "filterDateBefore", text: "Before" },
    { value: "lte", label: "filterDateOnOrBefore", text: "On or before" },
    { value: "gt", label: "filterDateAfter", text: "After" },
    { value: "gte", label: "filterDateOnOrAfter", text: "On or after" },
  ],
}
const NOT_EMPTY_OPERATOR = { value: "notEmpty", label: "filterNotEmpty", text: "Not empty" }
// `current`: a row's saved operator that this kind does not offer (an agent's "contains" on a number column) is kept
// as the last option, so the select always shows what the row does.
export function filterOperatorsFor(kind, current) {
  const options = [...(FILTER_OPERATORS[kind] || FILTER_OPERATORS.text), NOT_EMPTY_OPERATOR]
  if (!current || options.some((option) => option.value === current)) return options
  const known = [...FILTER_OPERATORS.text, ...FILTER_OPERATORS.number].find((option) => option.value === current)
  return [...options, known || { value: current, label: "", text: current }]
}
// The operator a row keeps when its column changes kind: the same one if the new kind offers it, else the first.
export function filterOperatorFor(kind, operator) {
  const options = filterOperatorsFor(kind)
  return options.some((option) => option.value === operator) ? operator : options[0].value
}

// Suggestions for a filter row's value (its <datalist>): the column's distinct non-blank values, sorted, at most
// `limit` of them; the input still takes any text.
export const FILTER_VALUE_HINTS = 200
export function filterValueHints(rows, column, limit = FILTER_VALUE_HINTS) {
  if (!column) return []
  const present = (rows || []).filter((row) => !isBlankCell(row?.[column]))
  // Review 2 §6: a fraction column shown ×100 hints its values as shown (0.052 → "5.2"), the scale the filter reads.
  const scale = filterScale(inferColumnFormat(column, (rows || []).slice(0, 1000).map((row) => row?.[column])))
  if (scale !== 1) {
    const shown = present.map((row) => {
      const number = numericCell(row[column])
      return { [column]: number == null ? row[column] : String(Number((number * scale).toPrecision(12))) }
    })
    return distinctFilterValues(shown, column).slice(0, limit)
  }
  return distinctFilterValues(present, column).slice(0, limit)
}

// The filter widget's options: the distinct values of the column, sorted (numbers by value,
// text alphabetically), from every row, not just the first few rows.
const FILTER_COLLATOR = typeof Intl !== "undefined" ? new Intl.Collator("en", { numeric: true, sensitivity: "base" }) : null
export function distinctFilterValues(rows, column) {
  const values = new Set()
  ;(rows || []).forEach((row) => values.add(String(row?.[column || ""] ?? "-")))
  return [...values].sort((first, second) => (FILTER_COLLATOR ? FILTER_COLLATOR.compare(first, second) : first < second ? -1 : 1))
}

// Chips shown before "+N more": the first `limit` values that match the search, plus any
// selected value past them (a selection never hides).
export const FILTER_CHIP_LIMIT = 12
export function visibleFilterValues(values, selected = [], search = "", limit = FILTER_CHIP_LIMIT) {
  const query = String(search || "").trim().toLowerCase()
  const matching = query ? values.filter((value) => value.toLowerCase().includes(query)) : values
  const shown = matching.slice(0, limit)
  const extra = matching.slice(limit).filter((value) => selected.includes(value))
  return { shown: [...shown, ...extra], hidden: matching.length - shown.length - extra.length, matching: matching.length }
}

// Whole-row viewport of a scrolling table (round 6): the tallest height that ends on a row
// boundary. `rowBottoms` are the body rows' bottom edges from the table top (header included),
// `available` the height the card gives the viewport. Returns undefined when everything fits or
// not even one body row does (the viewport then keeps the full height).
export function wholeRowsHeight(rowBottoms, available, headHeight = 0) {
  if (!Array.isArray(rowBottoms) || !rowBottoms.length || !(available > 0)) return undefined
  if (rowBottoms[rowBottoms.length - 1] <= available + 0.5) return undefined
  let fit
  for (const bottom of rowBottoms) {
    if (bottom > available + 0.5) break
    fit = bottom
  }
  return fit !== undefined && fit > headHeight ? Math.floor(fit) : undefined
}

// ── Date category axes (summer formatDateAxisValue, backlog #6) ─────────────────────────────

const AXIS_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const DAY_MS = 86_400_000

// The UTC day of an ISO date category ("2026-04-12", optionally with a time), or null.
export function chartDateValue(name) {
  const match = AXIS_DATE.exec(String(name ?? "").trim())
  if (!match) return null
  const time = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isFinite(time) && new Date(time).getUTCDate() === Number(match[3]) ? time : null
}

// Label granularity by the span of the axis, as in summer: under 2 months "12 Apr", under 2 years
// "Apr 2026", otherwise "2026". English month names, never the OS locale (§9).
export function dateAxisGranularity(times) {
  const span = (Math.max(...times) - Math.min(...times)) / DAY_MS
  if (span < 61) return "day"
  if (span < 730) return "month"
  return "year"
}

export function formatDateAxisLabel(time, granularity) {
  const date = new Date(time)
  const month = MONTH_SHORT[date.getUTCMonth()]
  if (granularity === "day") return `${date.getUTCDate()} ${month}`
  if (granularity === "month") return `${month} ${date.getUTCFullYear()}`
  return String(date.getUTCFullYear())
}

// The full date, for tooltips: "12 Apr 2026".
export function formatDateFull(time) {
  const date = new Date(time)
  return `${date.getUTCDate()} ${MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

// A date category axis, or null when the categories are not all ISO dates (2 or more).
// Returns { granularity, items (sorted by date unless the chart is ranked), full (tooltip names),
// ticks (axis labels), periodStart (true on the first item of each month or year) }.
export function dateCategoryAxis(items, options = {}) {
  if (!items || items.length < 2) return null
  const times = items.map((item) => chartDateValue(item.name))
  if (times.some((time) => time == null)) return null
  const order = items.map((item, index) => ({ item, time: times[index], index }))
  if (!options.keepOrder) order.sort((first, second) => first.time - second.time || first.index - second.index)
  const granularity = dateAxisGranularity(times)
  const ticks = order.map((entry) => formatDateAxisLabel(entry.time, granularity))
  return {
    granularity,
    items: order.map((entry) => entry.item),
    full: order.map((entry) => formatDateFull(entry.time)),
    ticks,
    periodStart: ticks.map((tick, index) => granularity === "day" || index === 0 || tick !== ticks[index - 1]),
  }
}

// Which date ticks to show on a `widthPx` plot: the first item of each period, thinned to every
// k-th period when the labels would collide (`labelPx` each, with a gap).
export function dateAxisTickIndexes(axis, widthPx, labelPx) {
  let starts = axis.periodStart.flatMap((start, index) => (start ? [index] : []))
  // The first item starts a partial period ("Nov 2025" on 20 Nov); drop its label when it would
  // touch the next period's ("Dec 2025" on 1 Dec).
  const pxPerItem = Math.max(0, widthPx) / Math.max(1, axis.ticks.length - 1)
  if (starts.length > 2 && axis.granularity !== "day" && (starts[1] - starts[0]) * pxPerItem < labelPx + 12)
    starts = starts.slice(1)
  const fit = Math.max(1, Math.floor(Math.max(0, widthPx) / Math.max(1, labelPx + 12)))
  const step = Math.max(1, Math.ceil(starts.length / fit))
  return new Set(starts.filter((_, index) => index % step === 0))
}

// ── Category labels ─────────────────────────────────────────────────────────────────────────

const LABEL_ACRONYMS = new Set(["ETF", "REIT", "REITS", "GCC", "USA", "US", "UK", "UAE", "EU", "QAR", "USD", "EUR", "GBP", "IT", "IPO", "ESG"])

function isAllCaps(text) {
  return /[A-Z]/.test(text) && !/[a-z]/.test(text)
}

// Sector names sent in ALL CAPS ("CONSUMER NON-CYCLICA") read in sentence case on chart axes
// ("Consumer non-cyclica"). Display only, and only when every label is in capitals and none looks
// like a code (a ticker "QNBK.QA", a digit, or every label a short single word like "QNBK").
export function sentenceCaseLabels(names) {
  const texts = (names || []).map((name) => String(name ?? ""))
  if (!texts.length || !texts.every(isAllCaps)) return texts
  if (texts.some((text) => /\d/.test(text) || /^\S+\.\S+$/.test(text.trim()))) return texts
  if (!texts.some((text) => /\s/.test(text.trim()) || text.replace(/[^A-Z]/g, "").length >= 6)) return texts
  return texts.map((text) => {
    const words = text.toLowerCase().split(/(\s+|-|\/|&)/)
    const cased = words.map((word) => (LABEL_ACRONYMS.has(word.toUpperCase()) ? word.toUpperCase() : word))
    const joined = cased.join("")
    const first = joined.search(/[A-Za-z]/)
    return first < 0 ? joined : `${joined.slice(0, first)}${joined.charAt(first).toUpperCase()}${joined.slice(first + 1)}`
  })
}

// Approximate text width at `fontPx`: capitals run ~20% wider than mixed case.
export function labelTextPx(text, fontPx) {
  const value = String(text ?? "")
  return value.length * fontPx * (/[a-z]/.test(value) ? 0.58 : 0.7)
}

function wrapLines(text, widthPx, fontPx) {
  const words = String(text ?? "").split(/\s+/).filter(Boolean)
  const lines = []
  for (const word of words) {
    if (labelTextPx(word, fontPx) > widthPx) return Infinity
    const last = lines[lines.length - 1]
    if (last != null && labelTextPx(`${last} ${word}`, fontPx) <= widthPx) lines[lines.length - 1] = `${last} ${word}`
    else lines.push(word)
  }
  return lines.length
}

// How a vertical bar chart shows its category names on a `plotWidthPx` plot:
// "flat" (one line), "wrap" (two lines, horizontal), "horizontal" (switch to horizontal bars:
// 5 or more categories that fit neither way, which is where the old axis started rotating) or
// "rotate" (24° labels, the last resort for 2-4 long names or an explicit vertical orientation).
// The plan said "more than 8"; on Misnad p3 that left the 7-sector benchmark chart rotated next
// to the 10-sector portfolio chart in horizontal bars, so the threshold follows the rotation rule.
export function categoryAxisLayout(names, plotWidthPx, fontPx, options = {}) {
  const count = Math.max(1, names.length)
  const band = Math.max(0, plotWidthPx) / count - 6
  if (names.every((name) => labelTextPx(name, fontPx) <= band)) return "flat"
  if (names.every((name) => wrapLines(name, band, fontPx) <= 2)) return "wrap"
  if (options.allowHorizontal !== false && names.length > (options.horizontalAfter ?? 4)) return "horizontal"
  return "rotate"
}

// ── Compact value axes (backlog #13) ────────────────────────────────────────────────────────

// The unit for a value axis whose largest |value| is `maxAbs`: K from 10,000, M from a million,
// B from a billion. Below 10,000 the full number reads fine.
export function compactAxisUnit(maxAbs) {
  const value = Math.abs(Number(maxAbs) || 0)
  if (value >= 1e9) return { divisor: 1e9, suffix: "B" }
  if (value >= 1e6) return { divisor: 1e6, suffix: "M" }
  if (value >= 1e4) return { divisor: 1e3, suffix: "K" }
  return null
}

const compactFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 })
export function formatCompactAxisValue(value, unit) {
  const number = numericCell(value)
  if (number == null || !unit) return null
  const scaled = number / unit.divisor
  return `${compactFormatter.format(Math.abs(scaled) < 0.005 ? 0 : scaled)}${scaled === 0 ? "" : unit.suffix}`
}

// ── Signed percentages in tables (backlog #7) ───────────────────────────────────────────────

// Summer renderSignedPercent (0424540f7): the factsheet green / red, AA on white.
export const SIGNED_COLORS = { up: "#067647", down: "#B42318" }
const SIGNED_WORDS = new Set(["return", "returns", "change", "changes", "delta", "diff", "difference", "performance", "growth", "excess", "alpha", "gain", "gains"])
const UNSIGNED_WORDS = new Set(["weight", "weights", "allocation", "allocations", "share", "coverage", "yield", "yields", "ytm", "eligible", "exposure"])

// A percent column of returns, changes or deltas (never weights, allocations or coverage).
// An explicit columnFormats entry decides first: `signed: true | false`, or the format
// "percent_change" / "return" (a percent that is always signed).
export function isSignedPercentColumn(column, format, label, explicit) {
  if (format?.kind !== "percent") return false
  if (typeof explicit?.signed === "boolean") return explicit.signed
  if (format.signed) return true
  const tokens = [...columnNameTokens(column), ...columnNameTokens(label)]
  if (tokens.some((token) => UNSIGNED_WORDS.has(token))) return false
  return tokens.some((token) => SIGNED_WORDS.has(token))
}

// "▲ 4.43%" (up), "▼ 1.20%" (down) or "0.00%" (no direction); null for a blank cell ("—").
export function signedPercentCell(value, format, locale) {
  const text = formatTableCell(value, format, locale)
  if (text == null) return null
  const number = numericCell(value)
  if (number == null) return { text, direction: null }
  const shown = number * (format.scale || 1)
  if (Math.abs(shown) < 0.5 * 10 ** -(format.decimals ?? 2)) return { text, direction: null }
  const magnitude = text.replace(/^[-−+]\s*/, "")
  return shown > 0 ? { text: `▲ ${magnitude}`, direction: "up" } : { text: `▼ ${magnitude}`, direction: "down" }
}

// ── One header across pages (backlog #8) ────────────────────────────────────────────────────

// Summer syncHeaderConfiguration (S/utils/headerWidget.js:54): editing one page's header updates
// the header on every page. The title and the style (logo, font size, the as-of date switch) are
// shared; each header keeps its own id and layout. The band colour is already one setting
// (config.design.headerColor).
export function syncHeaderWidgets(tabs, edited) {
  if (edited?.type !== "header") return tabs
  return (tabs || []).map((tab) => ({
    ...tab,
    widgets: (tab.widgets || []).map((widget) => {
      if (widget.type !== "header") return widget
      if (widget.id === edited.id) return edited
      return { ...widget, title: edited.title, style: { ...(widget.style || {}), ...(edited.style || {}) } }
    }),
  }))
}

// The header's as-of line is shown from this band height up (a thinner band keeps the title only).
export const HEADER_DATE_MIN_HEIGHT = 56

// ── Contributors / detractors rows (backlog #9) ─────────────────────────────────────────────

// Summer renderFactsheetContributionRows (DashboardWorkspaceWidget.jsx:567): label | bar | "x.xx%",
// the bar proportional to the largest magnitude but never under 16%, top 5. Contributors are the
// largest values first; detractors the most negative first, drawn mirrored (value | bar | label).
export const CONTRIBUTION_ROW_LIMIT = 5
export const CONTRIBUTION_MIN_BAR = 16
export function contributionRows(items, mode = "contributors", options = {}) {
  const limit = options.limit ?? CONTRIBUTION_ROW_LIMIT
  const scale = options.scale || 1
  const detractors = mode === "detractors"
  const numbered = (items || [])
    .filter((item) => !item?.other)
    .map((item) => ({ label: String(item.name ?? ""), value: Number(item.value) }))
    .filter((item) => Number.isFinite(item.value))
  // Review 2 §5: Contributors are the positive values only, Detractors the negative only; with 2 negatives and 8
  // positives, Detractors shows the 2 negatives, never the smallest positives drawn as red bars.
  const ranked = numbered
    .filter((item) => (detractors ? item.value < 0 : item.value > 0))
    .sort((first, second) => (detractors ? first.value - second.value : second.value - first.value))
  const shown = ranked.slice(0, Math.max(0, limit))
  const max = Math.max(...shown.map((item) => Math.abs(item.value)), 0)
  const decimals = options.decimals ?? 2
  return shown.map((item, index) => ({
    label: item.label,
    value: item.value,
    rank: index,
    width: max > 0 ? Math.max((Math.abs(item.value) / max) * 100, CONTRIBUTION_MIN_BAR) : CONTRIBUTION_MIN_BAR,
    text: `${formatDecimal(item.value * scale, decimals, options.locale || "en-US")}%`,
  }))
}

// Bar colours down the list: the accent fading for contributors (summer opacity ramp), the
// semantic red fading for detractors.
export function contributionColors(mode, accent = "#2f5ea8") {
  if (mode === "detractors") return ["#B42318", "#C8524A", "#D98480", "#E6AEAB", "#F0D3D1"]
  return [1, 0.78, 0.58, 0.42, 0.3].map((alpha) => hexAlpha(accent, alpha))
}

function hexAlpha(hex, alpha) {
  const value = String(hex || "").replace("#", "")
  if (!/^[0-9a-f]{6}$/i.test(value)) return hex
  if (alpha >= 1) return `#${value}`
  const [r, g, b] = [0, 2, 4].map((start) => parseInt(value.slice(start, start + 2), 16))
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// ── Chart legends (backlog #17) ─────────────────────────────────────────────────────────────

// Summer charts.js: a legend scrolls instead of wrapping over the plot. Series legends sit top-centre (16×8 round
// rects, room for the arrows); a pie's legend sits at the bottom and the pie is re-centred at 44% above it.
export const PIE_LEGEND_CENTER = ["50%", "44%"]
export function scrollLegend(position, fontPx = 12) {
  const base = {
    type: "scroll",
    left: "center",
    icon: "roundRect",
    itemWidth: 16,
    itemHeight: 8,
    itemGap: 16,
    padding: [2, 28],
    pageIconSize: 10,
    pageIconColor: "#667085",
    pageIconInactiveColor: "#d0d5dd",
    pageTextStyle: { color: "#667085", fontSize: Math.max(10, fontPx - 1) },
    textStyle: { color: "#475467", fontSize: fontPx },
  }
  return position === "bottom" ? { ...base, bottom: 0 } : { ...base, top: 0 }
}

// ── Explain: filters in words (backlog #19) ─────────────────────────────────────────────────

// Summer DashboardWidgetExplainModal lists the active filters in words. One chip per filter: "Sector is one of Banks,
// Energy", "Weight > 2", "As of on or after 1 Apr 2026", "Weight 2 – 5"; any other shape reads "Key: value", never JSON.
// `formatDate` turns an ISO day into the app's one date format (dates.ts formatDay); `labels` are the dashboard labels.
export const EXPLAIN_CHIP_VALUES = 3
const CHIP_WORDS = {
  contains: ["explainContains", "contains"],
  equals: ["explainIs", "is"],
  not_equals: ["explainIsNot", "is not"],
  gt: ["", ">"],
  gte: ["", "≥"],
  lt: ["", "<"],
  lte: ["", "≤"],
}
const CHIP_DATE_WORDS = {
  equals: ["filterDateOn", "On"],
  not_equals: ["filterDateNotOn", "Not on"],
  gt: ["filterDateAfter", "After"],
  gte: ["filterDateOnOrAfter", "On or after"],
  lt: ["filterDateBefore", "Before"],
  lte: ["filterDateOnOrBefore", "On or before"],
}
export function filterChipText(filter, options = {}) {
  if (!filter || typeof filter !== "object") return ""
  const labels = options.labels || {}
  const formatDate = options.formatDate || ((value) => value)
  const word = ([key, text]) => (key && labels[key]) || text
  const isDate = (value) => typeof value === "string" && ISO_DATE_PREFIX.test(value.trim())
  const show = (value) => (isDate(value) ? formatDate(value.trim().slice(0, 10)) || value : String(value ?? ""))
  const column = filter.column ?? filter.field ?? filter.label
  const name = column ? humanizeColumnName(column) || String(column) : ""
  const values = filter.values ?? filter.selectedValues ?? (Array.isArray(filter.value) ? filter.value : undefined)
  if (name && Array.isArray(values)) {
    if (!values.length) return ""
    const shown = values.slice(0, EXPLAIN_CHIP_VALUES).map(show).join(", ")
    const more = values.length > EXPLAIN_CHIP_VALUES ? ` +${values.length - EXPLAIN_CHIP_VALUES}` : ""
    return `${name} ${values.length > 1 ? word(["explainIsOneOf", "is one of"]) : word(CHIP_WORDS.equals)} ${shown}${more}`
  }
  const low = filter.gte ?? filter.min ?? filter.from
  const high = filter.lte ?? filter.max ?? filter.to
  if (name && (low != null || high != null) && !filter.operator) {
    if (low != null && high != null) return `${name} ${show(low)} – ${show(high)}`
    return low != null ? `${name} ≥ ${show(low)}` : `${name} ≤ ${show(high)}`
  }
  if (name && (filter.operator || "value" in filter)) {
    const operator = filter.operator || "contains"
    if (operator === "notEmpty") return `${name} ${word(["explainNotEmpty", "is not empty"])}`
    const words = isDate(filter.value) && CHIP_DATE_WORDS[operator] ? CHIP_DATE_WORDS[operator] : CHIP_WORDS[operator]
    const text = words ? word(words) : operator
    return `${name} ${words === CHIP_DATE_WORDS[operator] ? text.toLowerCase() : text} ${show(filter.value)}`.trim()
  }
  return Object.entries(filter)
    .filter(([key, value]) => key !== "id" && value != null && value !== "" && typeof value !== "object")
    .map(([key, value]) => `${humanizeColumnName(key) || key}: ${show(value)}`)
    .join(" · ")
}
// Every filter as one chip, duplicates and unfinished rows dropped.
export function explainFilterChips(filters, options = {}) {
  const chips = (Array.isArray(filters) ? filters : [])
    .filter((filter) => !(filter && "operator" in filter && "column" in filter) || filterIsApplied(filter))
    .map((filter) => filterChipText(filter, options))
    .filter(Boolean)
  return [...new Set(chips)]
}

// A pie (or donut) cannot draw a negative slice (summer: "Pie charts need non-negative values").
export function chartHasNegative(items) {
  return (items || []).some((item) => Number(item?.value) < 0)
}

// The first binding a chart's own data does not have, for a specific message under "Choose columns…" (#19).
// `{ kind: "missing" | "notNumeric", column }` or null.
export function chartBindingIssue(widget, table) {
  const data = widget?.data || {}
  const headers = table?.headers || []
  const rows = table?.rows || []
  const missing = (column) => column && column !== "__count__" && !headers.includes(column)
  const columns = [
    ...(data.filters || []).map((filter) => filter.column),
    data.sort?.column,
    ...(widget?.type === "scatter" ? [data.xColumn, data.yColumn, data.sizeColumn] : [data.label, ...(data.measures?.length ? data.measures : [data.metric]), data.topBy]),
  ].filter(Boolean)
  const absent = columns.find(missing)
  if (absent) return { kind: "missing", column: absent }
  if (!rows.length) return null
  const numeric = widget?.type === "scatter" ? [data.xColumn, data.yColumn, data.sizeColumn] : data.aggregation === "count" ? [data.topBy] : [...(data.measures?.length ? data.measures : [data.metric]), data.topBy]
  const flat = numeric.filter((column) => column && column !== "__count__").find((column) => !rows.some((row) => numericCell(row[column]) !== null))
  return flat ? { kind: "notNumeric", column: flat } : null
}

// ── Smart titles from the config (backlog #20) ──────────────────────────────────────────────

// Summer buildSmartTitle: "Sum of Weight", "Sum of Weight by Sector", "Weight, Return +1 by Date", a table's first three
// columns ("Name, Weight, Sector +2"). Only for widgets added by hand: they carry `isAutoTitle: true` until the
// author types a title; an agent's widget (no isAutoTitle) is never renamed. Null when there is too little to go on.
const SMART_AGGREGATIONS = {
  sum: ["aggregationSum", "Sum"],
  avg: ["aggregationAverage", "Average"],
  min: ["aggregationMin", "Minimum"],
  max: ["aggregationMax", "Maximum"],
  first: ["aggregationFirst", "First"],
  last: ["aggregationLast", "Last"],
}
const SMART_SINGLE = ["bar", "pie", "treemap", "funnel", "heatmap"]
export function buildSmartTitle(widget, options = {}) {
  const labels = options.labels || {}
  const data = widget?.data || {}
  const name = (column) => String(data.displayNames?.[column] || humanizeColumnName(column) || column || "")
  const of = (aggregation, column) => {
    if (aggregation === "count") return labels.smartTitleCount || "Count of rows"
    if (!column) return null
    const [key, text] = SMART_AGGREGATIONS[aggregation] || SMART_AGGREGATIONS.sum
    return (labels.smartTitleOf || "{aggregation} of {column}").replace("{aggregation}", labels[key] || text).replace("{column}", name(column))
  }
  const by = (measure, dimension) => (labels.smartTitleBy || "{measure} by {dimension}").replace("{measure}", measure).replace("{dimension}", name(dimension))
  const list = (columns, limit) => {
    const shown = columns.slice(0, limit).map(name).join(", ")
    return columns.length > limit ? `${shown} +${columns.length - limit}` : shown
  }
  const type = widget?.type
  if (type === "kpi") return of(data.aggregation, data.metric)
  if (type === "filter") return data.label ? name(data.label) : null
  if (type === "scatter") return data.xColumn && data.yColumn ? `${name(data.yColumn)} ${labels.smartTitleVs || "vs"} ${name(data.xColumn)}` : null
  if (type === "table") {
    const columns = data.columns?.length ? data.columns : options.headers || []
    return columns.length ? list(columns, 3) : options.datasetName || null
  }
  const measures = data.measures?.length ? data.measures : []
  if (["line", "multiSeries", "bar"].includes(type) && measures.length > 1) return data.label ? by(list(measures, 2), data.label) : list(measures, 2)
  if (SMART_SINGLE.includes(type) || type === "line" || type === "multiSeries") {
    const measure = of(data.aggregation, measures[0] || data.metric)
    if (measure && data.label) return by(measure, data.label)
    if (data.label) return (labels.smartTitleByOnly || "By {dimension}").replace("{dimension}", name(data.label))
    return measure
  }
  return null
}
// The widget after a change, with its smart title kept up to date: a typed title ends isAutoTitle; a data or type
// change on an auto-titled widget renames it. Text, title and header widgets are never touched.
export function withSmartTitle(widget, patch, options = {}) {
  if (!widget || ["text", "title", "header"].includes(widget.type)) return { ...widget, ...patch }
  if ("title" in patch && patch.title !== widget.title && !("isAutoTitle" in patch)) return { ...widget, ...patch, isAutoTitle: false }
  const next = { ...widget, ...patch }
  if (!next.isAutoTitle || !("data" in patch || "type" in patch)) return next
  const title = buildSmartTitle(next, options)
  return title ? { ...next, title } : next
}

// ── Universal filter by field (backlog #21) ─────────────────────────────────────────────────

// The column a filter widget filters `widget` on, or "": an explicit target for the widget's dataset wins (Bobur's
// targets[]); else a filter bound to a field (`data.field`, summer universal filter) applies to any widget whose data
// has that column. A filter widget never filters itself or another filter.
export function dashboardFilterColumn(filterWidget, widget, table) {
  if (!filterWidget || !widget || widget.type === "filter") return ""
  const target = (filterWidget.data?.targets || []).find((item) => item.dataset_id === widget.data?.dataset_id)
  if (target?.column) return target.column
  const field = filterWidget.data?.field
  if (!field) return ""
  const headers = table?.headers || []
  return headers.includes(field) ? field : ""
}
// The filters on `widget` from the page's filter widgets: `{ column, values }` (selected values) or `{ column, gte,
// lte }` (a range, summer range → gte/lte). Nothing selected means nothing filtered ("All").
export function dashboardFiltersFor(widget, table, filterWidgets) {
  return (filterWidgets || []).flatMap((filterWidget) => {
    const column = dashboardFilterColumn(filterWidget, widget, table)
    if (!column) return []
    const range = filterWidget.data?.range
    if (range && (range.gte != null || range.lte != null)) return [{ column, gte: range.gte, lte: range.lte }]
    const values = filterWidget.data?.selectedValues || []
    return values.length ? [{ column, values }] : []
  })
}
export function rowMatchesDashboardFilter(row, filter) {
  const cell = row?.[filter.column]
  if (filter.values) return filter.values.includes(String(cell ?? ""))
  const value = filterComparable(cell)
  const low = filter.gte != null ? filterComparable(String(filter.gte)) : null
  const high = filter.lte != null ? filterComparable(String(filter.lte)) : null
  if (value == null) return false
  return (low == null || value >= low) && (high == null || value <= high)
}

// ── Page templates bound by table name (backlog #23) ────────────────────────────────────────

// Summer dashboardTemplates `dataset_ref`: a template names its data by table name (stable across clients), and each
// client's dataset with that name is used. Names compare without case, spaces or punctuation; a ref may list several
// names, the first that matches wins; a dataset whose name contains the ref is the fallback.
const refKey = (value) => String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "")
export function resolveDatasetRef(ref, datasets) {
  const refs = (Array.isArray(ref) ? ref : [ref]).map(refKey).filter(Boolean)
  if (!refs.length) return undefined
  const items = (datasets || []).map((item) => ({ item, key: refKey(item.name ?? item.table_name ?? item.datasetID ?? item.id) }))
  for (const want of refs) {
    const exact = items.find((entry) => entry.key === want)
    if (exact) return exact.item
  }
  for (const want of refs) {
    const partial = items.find((entry) => entry.key.includes(want))
    if (partial) return partial.item
  }
  return undefined
}
// The table a widget reads: its dataset_id, else (another client's copy of a template page) its dataset_ref by name.
export function widgetOutputTable(outputTables, widget) {
  const tables = outputTables || []
  const id = widget?.data?.dataset_id
  const byID = id ? tables.find((table) => table.datasetID === id) : undefined
  if (byID || !widget?.data?.dataset_ref) return byID
  return resolveDatasetRef(widget.data.dataset_ref, tables)
}

// ── What the agent changed (backlog #25) ────────────────────────────────────────────────────

// The widgets that are new or different in `next` compared with `previous` (same dashboard), page by page, in page
// and reading order: `[{ id, tabId }]`. A removed widget is not listed (there is nothing to flash).
export function changedWidgetIds(previous, next) {
  const before = new Map()
  ;(previous?.tabs || []).forEach((tab) => (tab.widgets || []).forEach((widget) => before.set(widget.id, JSON.stringify(widget))))
  return (next?.tabs || []).flatMap((tab) =>
    [...(tab.widgets || [])]
      .sort((first, second) => (first.layout?.y ?? 0) - (second.layout?.y ?? 0) || (first.layout?.x ?? 0) - (second.layout?.x ?? 0))
      .filter((widget) => before.get(widget.id) !== JSON.stringify(widget))
      .map((widget) => ({ id: widget.id, tabId: tab.id })),
  )
}

// ── Multi-series: 100% stacked, a second axis, area, a line in a bar chart (backlog #22) ────────

// Summer ce62a76a1 (Modal :84–107, charts.js :1146–1170): grouped / stacked / 100%, `secondary_measures`,
// `line_measures`, `area_measures`. Here they are optional `style` fields, so older charts read as before:
//   stackSeries (Bobur's, stacked) · stackMode: "percent" (100%) · secondaryMeasures · lineMeasures (bar chart only)
//   · areaMeasures (line and multi-series).
// Returns, per measure, how it draws: { type: "bar" | "line", area, axis: 0 | 1, stack }.
export const STACK_MODES = ["grouped", "stacked", "percent"]
export function chartStackMode(style = {}) {
  if (style.stackMode === "percent") return "percent"
  if (style.stackMode === "stacked" || style.stackSeries) return "stacked"
  return "grouped"
}
export function multiSeriesPlan(type, measures = [], style = {}, options = {}) {
  const many = measures.length > 1
  const mode = many ? chartStackMode(style) : "grouped"
  const pick = (list) => new Set((Array.isArray(list) ? list : []).filter((measure) => measures.includes(measure)))
  const lines = type === "bar" && many ? pick(style.lineMeasures) : new Set()
  const areas = type === "line" || type === "multiSeries" ? pick(style.areaMeasures) : new Set()
  const right = many && !options.horizontal ? pick(style.secondaryMeasures) : new Set()
  // A line in a 100% bar chart keeps its own units, on the right axis.
  if (mode === "percent") lines.forEach((measure) => right.add(measure))
  // Every series on the right is no second axis at all.
  if (right.size === measures.length) right.clear()
  const plan = measures.map((measure) => {
    const line = type !== "bar" || lines.has(measure)
    const axis = right.has(measure) ? 1 : 0
    // Stacks hold the series of one kind on the left axis: bars in a bar chart, lines in a line chart.
    const stacks = mode !== "grouped" && axis === 0 && (type === "bar" ? !line : true)
    return { measure, type: line ? "line" : "bar", area: line && areas.has(measure), axis, stack: stacks ? "total" : undefined }
  })
  return { mode, plan, secondary: right.size > 0, percent: mode === "percent" }
}
// 100% stacked: each stacked measure as its share of the category's stacked total (absolute values), 0–100.
export function percentStackItems(items, measures) {
  return (items || []).map((item) => {
    const total = measures.reduce((sum, measure) => sum + Math.abs(Number(item.values?.[measure]) || 0), 0)
    const values = { ...(item.values || {}) }
    measures.forEach((measure) => {
      const value = Number(item.values?.[measure]) || 0
      values[measure] = total ? Math.round((value / total) * 100 * 1e6) / 1e6 : 0
    })
    return { ...item, values }
  })
}
